import { Location, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { take } from 'rxjs/operators';
import type { EChartsOption } from 'echarts';
import { BusinessFuncsService } from '../businessfuncs/shared/businessfuncs.service';
import { VisualCmptBaseComponent } from '../shared/components/visual-cmpt-base.component';
import { IDEA_STATUS_GROUPING_REJECTED_CATEGORY_ID } from '../ideastatusgroupingcategories/shared/idea-status-grouping.constants';
import { EventsService } from '../shared/services/events.service';
import { UserContextService } from '../shared/services/user-context.service';
import { IUserScoringsService } from '../users/shared/iuser-scorings.service';
import { IUserScoringQuery } from '../users/shared/iuser-scoring-query.model';
import { IUserScoring } from '../users/shared/iuser-scoring.model';
import { Title } from '@angular/platform-browser';
import { IBFIdeaStatusesGrouped } from '../businessfuncs/shared/ibf-ideastatusesgrouped.model';
import { IWelcomeGroupingStatRow } from './welcome-grouping-stat.model';
import { WelcomeActivityFeedFilterService } from './welcome-activity-feed-filter.service';
import { UsersService } from '../users/shared/users.service';
import { toCssHexColor } from '../shared/utils/css-color.util';
import { ActivityEventsComponent } from '../activityevents/activity-events.component';
import { UiAvatarComponent } from '../shared/components/ui-avatar/ui-avatar.component';
import { LoadingUiBlockerComponent } from '../shared/components/loading-ui-blocker/loading-ui-blocker.component';
import { NgxEchartsDirective } from 'ngx-echarts';
import { GroupingStatTotalPipe } from '../shared/pipes/grouping-stat-total.pipe';

@Component({
    selector: 'app-welcome',
    templateUrl: './welcome.component.html',
    styleUrls: ['./welcome.component.scss'],
    imports: [
      ActivityEventsComponent,
      UiAvatarComponent,
      LoadingUiBlockerComponent,
      RouterModule,
      NgClass,
      NgxEchartsDirective,
      GroupingStatTotalPipe,
    ],
})
export class WelcomeComponent extends VisualCmptBaseComponent {
  groupingStatRows: IWelcomeGroupingStatRow[] = [];
  rating: IUserScoring[];
  monthChartOption: EChartsOption | null = null;
  private readonly monthChartBarColorBottom = '#4c83d6';
  private readonly monthChartBarColorTop = '#8db0e6';
  championAvatarUrls: Record<number, string> = {};
  private loadingChampionAvatarIds = new Set<number>();
  private failedChampionAvatarIds = new Set<number>();

  constructor(
    protected events: EventsService,
    protected bfsSvc: BusinessFuncsService,
    protected userScoringSvc: IUserScoringsService,
    protected userContextSvc: UserContextService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected location: Location,
    private titleService: Title,
    private welcomeFeedFilter: WelcomeActivityFeedFilterService,
    private usersSvc: UsersService,
    private cdr: ChangeDetectorRef
  ) {
    super(events, router, route, location);
  }

  ngOnInit() {
    super.ngOnInit();
    this.titleService.setTitle(this.userContextSvc.currentUser?.SelectedBF?.Title);
    this.load();
  }

  selectGroupingRow(row: IWelcomeGroupingStatRow) {
    this.welcomeFeedFilter.toggleFromStatRow(row.Title, row.GroupingCategoryId);
  }

  load() {
    this.loading = true;
    forkJoin({
      stats: this.loadStat(),
      ratings: this.loadRating(),
      ideasByMonth: this.loadIdeasByMonth(),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ stats, ratings, ideasByMonth }) => {
          this.rating = ratings.Items;
          this.groupingStatRows = this.buildGroupingStatRows(stats);
          this.applyMonthChart(ideasByMonth);
          this.prefetchChampionPhotos();
        },
      });
  }

  private parseNumber(v: unknown): number | undefined {
    if (v == null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }

  private parsePositiveId(v: unknown): number | undefined {
    const n = this.parseNumber(v);
    return n != null && n > 0 ? n : undefined;
  }

  /** PascalCase / camelCase как в JSON с бэка. */
  private normalizeGroupedStat(raw: unknown): IBFIdeaStatusesGrouped {
    const r = raw as Record<string, unknown>;
    const groupingCategoryId =
      this.parseNumber(r.GroupingCategoryId) ?? this.parseNumber(r.groupingCategoryId);
    const cntRaw = this.parseNumber(r.Cnt) ?? this.parseNumber(r.cnt);
    const sortRaw =
      this.parseNumber(r.GroupingSortOrder) ?? this.parseNumber(r.groupingSortOrder);
    const titleRaw = r.GroupingCategoryTitle ?? r.groupingCategoryTitle;
    const colorRaw = r.GroupingDisplayColor ?? r.groupingDisplayColor;
    const iconRaw = r.GroupingIconClass ?? r.groupingIconClass;
    return {
      BusinessFunctionId:
        this.parsePositiveId(r.BusinessFunctionId) ??
        this.parsePositiveId(r.businessFunctionId),
      GroupingCategoryId: groupingCategoryId ?? undefined,
      Cnt: cntRaw ?? 0,
      GroupingCategoryTitle: typeof titleRaw === 'string' ? titleRaw : undefined,
      GroupingDisplayColor: typeof colorRaw === 'string' ? colorRaw : undefined,
      GroupingIconClass: typeof iconRaw === 'string' ? iconRaw : undefined,
      GroupingSortOrder: sortRaw,
    };
  }

  private buildGroupingStatRows(stats: IBFIdeaStatusesGrouped[]): IWelcomeGroupingStatRow[] {
    const rows = stats.filter((x) => x.GroupingCategoryId != null && x.GroupingCategoryId > 0);
    rows.sort((a, b) => {
      const sa = a.GroupingSortOrder ?? a.GroupingCategoryId ?? 0;
      const sb = b.GroupingSortOrder ?? b.GroupingCategoryId ?? 0;
      if (sa !== sb) {
        return sa - sb;
      }
      return (a.GroupingCategoryId ?? 0) - (b.GroupingCategoryId ?? 0);
    });
    return rows.map((meta) => ({
      GroupingCategoryId: meta.GroupingCategoryId as number,
      Title: meta.GroupingCategoryTitle ?? '',
      Count: meta.Cnt ?? 0,
      displayColor: toCssHexColor(meta.GroupingDisplayColor) ?? undefined,
      iconClass: meta.GroupingIconClass,
      sortOrder: meta.GroupingSortOrder,
    }));
  }

  getGroupingStatIconClass(row: IWelcomeGroupingStatRow): string {
    const c = row.iconClass?.trim();
    return c || 'fas fa-lightbulb';
  }

  /** Резерв, если с бэка не пришёл hex (основной фон — style.displayColor). */
  getGroupingStatToneClass(row: IWelcomeGroupingStatRow): string {
    if (row.displayColor?.trim()) {
      return '';
    }
    return row.GroupingCategoryId === IDEA_STATUS_GROUPING_REJECTED_CATEGORY_ID
      ? 'is-red'
      : 'is-blue';
  }

  getChampionTrophyClass(index: number): string {
    if (index === 0) return 'is-gold';
    if (index === 1) return 'is-silver';
    if (index === 2) return 'is-bronze';
    return 'is-muted';
  }

  getChampionUserId(userRating: IUserScoring): number | undefined {
    const id = userRating?.User?.Id ?? userRating?.UserId;
    return id && id > 0 ? id : undefined;
  }

  championPhotoUrl(userRating: IUserScoring): string | undefined {
    const id = this.getChampionUserId(userRating);
    return id ? this.championAvatarUrls[id] : undefined;
  }

  private prefetchChampionPhotos() {
    for (const r of this.rating || []) {
      const userId = this.getChampionUserId(r);
      const email = (r.User?.Email || r.User?.PersonalEmail)?.trim();
      if (!userId || !email) {
        continue;
      }
      if (
        this.championAvatarUrls[userId] ||
        this.loadingChampionAvatarIds.has(userId) ||
        this.failedChampionAvatarIds.has(userId)
      ) {
        continue;
      }
      this.loadingChampionAvatarIds.add(userId);
      this.usersSvc
        .getProfilePhotoByEmail(email)
        .pipe(take(1))
        .subscribe({
          next: (blob) => {
            if (!blob || blob.size === 0) {
              this.failedChampionAvatarIds.add(userId);
              this.loadingChampionAvatarIds.delete(userId);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              this.championAvatarUrls[userId] = reader.result as string;
              this.loadingChampionAvatarIds.delete(userId);
              this.cdr.markForCheck();
            };
            reader.readAsDataURL(blob);
          },
          error: () => {
            this.failedChampionAvatarIds.add(userId);
            this.loadingChampionAvatarIds.delete(userId);
            this.cdr.markForCheck();
          },
        });
    }
  }

  private applyMonthChart(
    buckets: { year: number; month: number; count: number }[]
  ): void {
    if (!buckets?.length) {
      this.monthChartOption = null;
      return;
    }

    const labels = buckets.map((b) => {
      const d = new Date(b.year, b.month - 1, 1);
      return d.toLocaleDateString('ru-RU', { month: 'short' }).replace(/\.$/, '');
    });
    const data = buckets.map((b) => b.count);

    this.monthChartOption = {
      grid: { left: 6, right: 6, top: 10, bottom: 4, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        formatter: (params) => {
          const p = (Array.isArray(params) ? params[0] : params) as {
            dataIndex: number;
            value: number | string;
          };
          const idx = p.dataIndex;
          const b = buckets[idx];
          let m = new Date(b.year, b.month - 1, 1).toLocaleDateString('ru-RU', {
            month: 'long',
          });
          m = m.charAt(0).toUpperCase() + m.slice(1);
          const n = Number(p.value) || 0;
          return `${m}<br/>${n} ${this.ideasCountWordRu(n)}`;
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: '#8a93a6', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        minInterval: 1,
        splitLine: { lineStyle: { color: '#edf0f6' } },
        axisLabel: { color: '#8a93a6', fontSize: 11 },
      },
      series: [
        {
          name: 'Идеи',
          type: 'bar',
          data,
          barMaxWidth: 40,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: this.monthChartBarColorTop },
                { offset: 1, color: this.monthChartBarColorBottom },
              ],
            },
          },
        },
      ],
    };
  }

  private ideasCountWordRu(n: number): string {
    const k = Math.abs(Math.trunc(n)) % 100;
    if (k >= 11 && k <= 14) {
      return 'идей';
    }
    const d = Math.abs(Math.trunc(n)) % 10;
    if (d === 1) return 'идея';
    if (d >= 2 && d <= 4) return 'идеи';
    return 'идей';
  }

  private normalizeMonthBucket(raw: unknown): { year: number; month: number; count: number } {
    const r = raw as Record<string, unknown>;
    const year = this.parseNumber(r.Year) ?? this.parseNumber(r.year) ?? 0;
    const month = this.parseNumber(r.Month) ?? this.parseNumber(r.month) ?? 0;
    const count = this.parseNumber(r.Count) ?? this.parseNumber(r.count) ?? 0;
    return { year, month, count: Math.max(0, count) };
  }

  loadIdeasByMonth() {
    const bfId = this.userContextSvc.currentUser.SelectedBF?.Id;
    if (bfId == null || bfId <= 0) {
      return of([] as { year: number; month: number; count: number }[]);
    }
    return this.bfsSvc.getBFIdeasCreatedByMonth(bfId).pipe(
      map((raw) =>
        (Array.isArray(raw) ? raw : []).map((x) => this.normalizeMonthBucket(x))
      ),
      catchError((err) => {
        this.showErrorMessage('Загрузка', err);
        return of([]);
      })
    );
  }

  loadStat() {
    const bfId = this.userContextSvc.currentUser.SelectedBF?.Id;
    return this.bfsSvc.getBFsIdeaStatusesGrouped(bfId).pipe(
      map((raw) => (Array.isArray(raw) ? raw : []).map((x) => this.normalizeGroupedStat(x))),
      catchError((err) => {
        this.showErrorMessage('Загрузка', err);
        return of([]);
      })
    );
  }

  loadRating() {
    let query: IUserScoringQuery = {};
    this.loading = true;
    query.Skip = 0;
    query.Take = 3;
    query.BusinessFunctionIds = this.userContextSvc.currentUser.SelectedBF
      ? [this.userContextSvc.currentUser.SelectedBF.Id]
      : [];
    query.Sorts = [{ Field: 'Score', Desc: true }];
    return this.userScoringSvc.fetchPaged(query).pipe(
      catchError((err) => {
        this.showErrorMessage('Загрузка', err);
        return of({ Items: [] });
      })
    );
  }
}
